// src/pages/admin/AdminTickets.js
// Maxula
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { FiSend, FiArrowLeft, FiLifeBuoy, FiClock, FiMessageSquare, FiUser } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { ticketAPI } from '../../services/api';

const STATUS = {
  open: { cls: 'bg-blue-50 text-blue-700 ring-1 ring-blue-200', dot: 'bg-blue-500', label: 'Ouvert' },
  pending: { cls: 'bg-amber-50 text-amber-700 ring-1 ring-amber-200', dot: 'bg-amber-500', label: 'En attente' },
  closed: { cls: 'bg-gray-100 text-gray-500 ring-1 ring-gray-200', dot: 'bg-gray-400', label: 'Fermé' }
};
const statusOf = (s) => STATUS[s] || STATUS.open;

const FILTERS = [
  { v: '', label: 'Tous' },
  { v: 'open', label: 'Ouverts' },
  { v: 'pending', label: 'En attente' },
  { v: 'closed', label: 'Fermés' }
];

// Date robuste (les messages stockent `at` ; certains objets `createdAt`).
const fmtDateTime = (v) => {
  const d = new Date(v);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};
const fmtDate = (v) => {
  const d = new Date(v);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
};

const clientName = (t) => {
  const u = t?.userId;
  if (!u || typeof u !== 'object') return 'Client';
  return `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email || 'Client';
};

const StatusBadge = ({ status }) => {
  const s = statusOf(status);
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${s.cls}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {s.label}
    </span>
  );
};

const AdminTickets = () => {
  const [tickets, setTickets] = useState([]);
  const [active, setActive] = useState(null);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const threadRef = useRef(null);

  const load = useCallback(async (st) => {
    try { setLoading(true); const res = await ticketAPI.adminList(st || undefined); setTickets(res.data?.data || []); }
    catch (e) {} finally { setLoading(false); }
  }, []);
  useEffect(() => { load(filter); }, [filter, load]);

  const open = async (id) => {
    try { const res = await ticketAPI.getOne(id); setActive(res.data?.data); }
    catch (e) { toast.error('Erreur lors de l\'ouverture'); }
  };
  const send = async () => {
    if (!reply.trim() || sending) return;
    try {
      setSending(true);
      const res = await ticketAPI.reply(active._id || active.id, reply.trim());
      setActive(res.data?.data); setReply(''); load(filter);
    } catch (e) { toast.error('Erreur lors de l\'envoi'); }
    finally { setSending(false); }
  };
  const setStatus = async (s) => {
    try {
      await ticketAPI.setStatus(active._id || active.id, s);
      const res = await ticketAPI.getOne(active._id || active.id);
      setActive(res.data?.data); load(filter); toast.success('Statut mis à jour');
    } catch (e) { toast.error('Erreur de mise à jour'); }
  };

  useEffect(() => {
    if (active && threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight;
  }, [active]);

  // ---------- Vue détail (conversation) ----------
  if (active) {
    const msgs = Array.isArray(active.messages) ? active.messages : [];
    const isClosed = active.status === 'closed';
    const cName = clientName(active);
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <button onClick={() => setActive(null)} className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-primary-600 mb-4">
          <FiArrowLeft /> Tous les tickets
        </button>

        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          {/* En-tête ticket */}
          <div className="px-5 py-4 border-b border-gray-100 bg-gray-50/60 flex items-start justify-between gap-3">
            <div>
              <h2 className="text-lg font-bold text-gray-900">{active.subject}</h2>
              <div className="flex items-center flex-wrap gap-3 mt-1 text-xs text-gray-400">
                <span className="inline-flex items-center gap-1"><FiUser size={12} /> {cName}</span>
                <span className="inline-flex items-center gap-1"><FiClock size={12} /> {fmtDate(active.createdAt) || '—'}</span>
                <span className="inline-flex items-center gap-1"><FiMessageSquare size={12} /> {msgs.length} message{msgs.length > 1 ? 's' : ''}</span>
              </div>
            </div>
            <select
              value={active.status}
              onChange={(e) => setStatus(e.target.value)}
              className="text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 bg-white focus:ring-2 focus:ring-primary-200 focus:border-primary-400 outline-none shrink-0"
            >
              <option value="open">Ouvert</option>
              <option value="pending">En attente</option>
              <option value="closed">Fermé</option>
            </select>
          </div>

          {/* Fil de conversation (admin = droite/rose, client = gauche/gris) */}
          <div ref={threadRef} className="px-5 py-5 space-y-4 max-h-[26rem] overflow-y-auto bg-gray-50/30">
            {msgs.length === 0 && <div className="text-center text-sm text-gray-400 py-6">Aucun message.</div>}
            {msgs.map((m, i) => {
              const fromAdmin = m.senderRole === 'admin';
              const when = fmtDateTime(m.at || m.createdAt);
              return (
                <div key={i} className={`flex items-end gap-2 ${fromAdmin ? 'justify-end' : 'justify-start'}`}>
                  {!fromAdmin && (
                    <div className="w-8 h-8 rounded-full bg-gray-200 text-gray-600 flex items-center justify-center shrink-0">
                      <FiUser size={15} />
                    </div>
                  )}
                  <div className={`max-w-[78%] ${fromAdmin ? 'items-end' : 'items-start'} flex flex-col`}>
                    <div className={`px-4 py-2.5 text-sm whitespace-pre-wrap leading-relaxed shadow-sm ${
                      fromAdmin
                        ? 'bg-primary-600 text-white rounded-2xl rounded-br-md'
                        : 'bg-white border border-gray-200 text-gray-800 rounded-2xl rounded-bl-md'
                    }`}>
                      {m.body}
                    </div>
                    <div className="text-[11px] text-gray-400 mt-1 px-1">
                      {fromAdmin ? 'Vous (admin)' : cName}{when ? ` · ${when}` : ''}
                    </div>
                  </div>
                  {fromAdmin && (
                    <div className="w-8 h-8 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center shrink-0">
                      <FiLifeBuoy size={15} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Zone de réponse */}
          <div className="px-5 py-4 border-t border-gray-100 bg-white">
            {isClosed ? (
              <div className="text-center text-sm text-gray-400 py-1">Ce ticket est fermé. Rouvrez-le (statut ci-dessus) pour répondre.</div>
            ) : (
              <div className="flex items-center gap-2">
                <input
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                  placeholder="Répondre…"
                  className="flex-1 border border-gray-200 rounded-full px-4 py-2.5 text-sm focus:ring-2 focus:ring-primary-200 focus:border-primary-400 outline-none"
                />
                <button
                  onClick={send}
                  disabled={sending || !reply.trim()}
                  className="w-11 h-11 rounded-full bg-primary-600 text-white flex items-center justify-center hover:bg-primary-700 disabled:opacity-50 shrink-0"
                  aria-label="Envoyer"
                >
                  <FiSend size={16} />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ---------- Vue liste ----------
  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-900 mb-1">Tickets de support</h1>
      <p className="text-sm text-gray-500 mb-5">Gérez les demandes des marchands et clients.</p>

      <div className="flex flex-wrap gap-2 mb-5">
        {FILTERS.map((f) => (
          <button
            key={f.v || 'all'}
            onClick={() => setFilter(f.v)}
            className={`px-3.5 py-1.5 rounded-full text-sm font-medium border transition-colors ${
              filter === f.v
                ? 'bg-primary-600 text-white border-primary-600'
                : 'bg-white text-gray-600 border-gray-200 hover:border-primary-300'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" /></div>
      ) : tickets.length === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-gray-200 py-16 text-center">
          <div className="w-14 h-14 mx-auto rounded-full bg-primary-50 text-primary-600 flex items-center justify-center mb-3">
            <FiLifeBuoy size={26} />
          </div>
          <p className="text-gray-700 font-semibold">Aucun ticket</p>
          <p className="text-sm text-gray-400 mt-1">Aucun ticket ne correspond à ce filtre.</p>
        </div>
      ) : (
        <div className="space-y-2.5">
          {tickets.map((t) => {
            const count = Array.isArray(t.messages) ? t.messages.length : (t.messages?.length || 0);
            return (
              <button
                key={t._id || t.id}
                onClick={() => open(t._id || t.id)}
                className="group w-full text-left bg-white rounded-2xl border border-gray-200 p-4 hover:border-primary-300 hover:shadow-sm transition-all flex items-center justify-between gap-3"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 rounded-xl bg-primary-50 text-primary-600 flex items-center justify-center shrink-0 group-hover:bg-primary-100">
                    <FiMessageSquare size={18} />
                  </div>
                  <div className="min-w-0">
                    <div className="font-semibold text-gray-900 truncate">{t.subject}</div>
                    <div className="text-xs text-gray-400 mt-0.5 truncate">
                      <span className="inline-flex items-center gap-1"><FiUser size={11} /> {clientName(t)}</span>
                      {' · '}{count} message{count > 1 ? 's' : ''}
                      {fmtDate(t.updatedAt || t.createdAt) ? ` · ${fmtDate(t.updatedAt || t.createdAt)}` : ''}
                    </div>
                  </div>
                </div>
                <StatusBadge status={t.status} />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminTickets;
